import React, { useState, useRef } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Container, 
  Paper, 
  Grid, 
  Link, 
  Checkbox, 
  FormControlLabel,
  Divider,
  IconButton,
  CircularProgress
} from '@mui/material';
import { 
  GitHub as GitHubIcon, 
  Google as GoogleIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import EmailVerification from '../components/auth/EmailVerification';
import { useTranslation } from 'react-i18next';
import './AuthPages.css';

const LoginPage = () => {
  const { t } = useTranslation(['auth', 'common', 'validation']);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  const { login, requiresEmailVerification, emailToVerify, handleEmailVerificationSuccess } = useAuth();
  const navigate = useNavigate();

  // Si l'email nécessite une vérification, afficher le composant de vérification
  if (requiresEmailVerification) {
    return (
      <EmailVerification 
        email={emailToVerify} 
        onVerificationSuccess={() => {
          handleEmailVerificationSuccess();
          navigate('/dashboard');
        }} 
      />
    );
  }

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setFieldErrors(prev => ({
      ...prev,
      email: !value ? t('validation:email.required') : 
             !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? t('validation:email.invalid') : ''
    }));
    setError('');
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setFieldErrors(prev => ({
      ...prev,
      password: !value ? t('validation:password.required') : ''
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    
    if (!email) {
      newErrors.email = t('validation:email.required');
      emailInputRef.current?.focus();
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t('validation:email.invalid');
      emailInputRef.current?.focus();
    }
    
    if (!password) {
      newErrors.password = t('validation:password.required');
      if (!newErrors.email) passwordInputRef.current?.focus();
    }
    
    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      return;
    }
    
    setLoading(true);
    setError('');
    setFieldErrors({});
    
    try {
      const result = await login(email, password);
      
      if (result.success) {
        navigate('/dashboard');
      } else if (!result.requiresEmailVerification) {
        setError(result.message || t('auth.login.error.unexpected'));
      }
    } catch (err) {
      setError(t('auth.login.error.unexpected'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const clearError = () => {
    setError('');
  };

  return (
    <Container component="main" maxWidth="xs" sx={{ py: { xs: 4, md: 8 } }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* Header Style Like Payment History */}
          <Box 
            className="auth-page-header" 
            sx={{ 
              width: 'calc(100% + 32px)', 
              margin: { xs: '-8px -16px 20px -16px', sm: '-16px -32px 20px -32px' },
              mb: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Typography component="h1" sx={{ fontWeight: 600, fontSize: { xs: '1.3rem', sm: '1.5rem' }, width: '100%', textAlign: 'center' }}>
              {t('auth.login.title')}
            </Typography>
            <Typography variant="body2" sx={{ display: 'block', width: '100%', textAlign: 'center', color: '#ffffff', mt: 1 }}>
              {t('auth.login.noAccount')} {' '}
              <Link component={RouterLink} to="/register" variant="body2" sx={{ color: '#ffffff', textDecoration: 'underline' }}>
                {t('common:navigation.register')}
              </Link>
            </Typography>
          </Box>
          
          {/* Error Alert Container */}
          {error && (
            <Box className="auth-error-container" sx={{ width: '100%' }}>
              <Box className="auth-error-alert">
                <Box sx={{ flex: 1, fontSize: '0.95rem' }}>
                  {error}
                </Box>
                <CloseIcon 
                  className="auth-error-close" 
                  sx={{ cursor: 'pointer', ml: 2, fontSize: '1.2rem' }}
                  onClick={clearError}
                />
              </Box>
            </Box>
          )}
          
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%', mt: 2 }}>
            {/* Email Field */}
            <Box sx={{ mb: 2 }}>
              <TextField
                inputRef={emailInputRef}
                margin="dense"
                required
                fullWidth
                id="email"
                label={t('auth.login.email')}
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={handleEmailChange}
                error={!!fieldErrors.email}
                helperText={fieldErrors.email}
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    transition: 'all 0.2s ease',
                  }
                }}
              />
            </Box>
            
            {/* Password Field */}
            <Box sx={{ mb: 2 }}>
              <TextField
                inputRef={passwordInputRef}
                margin="dense"
                required
                fullWidth
                name="password"
                label={t('auth.register.password')}
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={handlePasswordChange}
                error={!!fieldErrors.password}
                helperText={fieldErrors.password}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label={t('auth.register.togglePasswordVisibility')}
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      disabled={loading}
                      size="small"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    transition: 'all 0.2s ease',
                  }
                }}
              />
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox 
                    value="remember" 
                    color="primary" 
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={loading}
                  />
                }
                label={t('auth.login.rememberMe')}
              />
              
              <Link component={RouterLink} to="/forgot-password" variant="body2">
                {t('auth.login.forgotPassword')}
              </Link>
            </Box>
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              disabled={loading}
              className="auth-submit-button"
              sx={{ 
                mt: 1, 
                mb: 3,
                background: loading ? undefined : 'linear-gradient(135deg, #6200ee 0%, #03dac4 100%)',
                minHeight: '44px'
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  <span>{t('common.loading')}</span>
                </Box>
              ) : (
                t('auth.login.title')
              )}
            </Button>            
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginPage;